import { ethers } from 'ethers';
import { getProvider } from '../utils/web3';
import { CONTRACT_ADDRESS, CONTRACT_ABI, USDT_ADDRESS, USDT_ABI, PACKAGE_PRICES, NETWORK_CONFIG } from '../config/constants';

export const getContract = (signerOrProvider) => {
  return new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signerOrProvider);
};

export const getUSDTContract = (signerOrProvider) => {
  return new ethers.Contract(USDT_ADDRESS, USDT_ABI, signerOrProvider);
};

// Check if connected to correct network
export const checkNetwork = async () => {
  try {
    if (!window.ethereum) {
      throw new Error('No wallet detected');
    }
    
    const chainId = await window.ethereum.request({ method: 'eth_chainId' });
    const expectedChainId = NETWORK_CONFIG.chainId.toLowerCase();
    
    if (chainId.toLowerCase() !== expectedChainId) {
      console.warn(`Wrong network. Expected: ${expectedChainId}, Got: ${chainId}`);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error checking network:', error);
    return false;
  }
};

// Verify contract exists
export const verifyContract = async () => {
  try {
    const provider = getProvider();
    const code = await provider.getCode(CONTRACT_ADDRESS);
    
    if (code === '0x' || code === '0x0') {
      console.error('No contract found at address:', CONTRACT_ADDRESS);
      return false;
    }
    
    console.log('Contract verified at:', CONTRACT_ADDRESS);
    return true;
  } catch (error) {
    console.error('Error verifying contract:', error);
    return false;
  }
};

export const getUserId = async (address) => {
  try {
    // Ensure we have a valid address
    if (!address || !ethers.utils.isAddress(address)) {
      console.error('Invalid address provided to getUserId:', address);
      return '0';
    }
    
    // Check network first
    const isCorrectNetwork = await checkNetwork();
    if (!isCorrectNetwork) {
      console.error('Please switch to', NETWORK_CONFIG.chainName);
      return '0';
    }
    
    // Create fresh provider instance - Trust Wallet compatible pattern
    if (!window.ethereum) {
      throw new Error('No wallet detected');
    }
    
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    
    // Verify network is ready
    try {
      await provider.getNetwork();
    } catch (netError) {
      console.warn('Network not ready, retrying...', netError);
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    const contract = getContract(provider);
    const userId = await contract.addressToId(address);
    
    console.log('getUserId result for', address.substring(0, 10) + '...:', userId.toString());
    return userId.toString();
  } catch (error) {
    console.error('Error getting user ID:', error);
    console.error('Contract Address:', CONTRACT_ADDRESS);
    console.error('Network:', NETWORK_CONFIG.chainName, NETWORK_CONFIG.chainId);
    
    // Check if contract exists
    await verifyContract();
    
    return '0';
  }
};

export const getUserDetails = async (userId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const details = await contract.getUserDetails(userId);
    
    return {
      account: details[0],
      id: details[1].toString(),
      referrer: details[2].toString(),
      isActive: details[3],
      totalDirectReferrals: details[4].toString(),
      claimableAmount: details[5].toString(),
      totalClaimed: details[6].toString(),
      totalIncome: details[7].toString()
    };
  } catch (error) {
    console.error('Error getting user details:', error);
    throw error;
  }
};

export const getUserPackages = async (userId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const packages = await contract.getUserPackages(userId);
    return packages;
  } catch (error) {
    console.error('Error getting user packages:', error);
    throw error;
  }
};

export const getUserPackageDetails = async (userId, packageId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const details = await contract.getUserPackageDetails(userId, packageId);
    
    return {
      isActive: details[0],
      activationTime: details[1].toString(),
      totalInvested: details[2].toString(),
      totalEarned: details[3].toString(),
      ceilingLimit: details[4].toString(),
      remainingLimit: details[5].toString()
    };
  } catch (error) {
    console.error('Error getting package details:', error);
    throw error;
  }
};

export const getUserIncomeBreakdown = async (userId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const breakdown = await contract.getUserIncomeBreakdown(userId);
    
    return {
      directReferralIncome: breakdown[0].toString(),
      matrixLevelIncome: breakdown[1].toString(),
      normalLevelIncome: breakdown[2].toString(),
      poolIncome: breakdown[3].toString(),
      royaltyIncome: breakdown[4].toString(),
      totalIncome: breakdown[5].toString(),
      claimableAmount: breakdown[6].toString(),
      totalClaimed: breakdown[7].toString()
    };
  } catch (error) {
    console.error('Error getting income breakdown:', error);
    throw error;
  }
};

export const getUserReferrals = async (userId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const referrals = await contract.getUserReferrals(userId);
    return referrals.map(ref => ref.toString());
  } catch (error) {
    console.error('Error getting referrals:', error);
    return [];
  }
};

export const getMatrixDetails = async (userId, packageId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const details = await contract.getMatrixDetails(userId, packageId);
    
    return {
      upline: details[0].toString(),
      directDownlines: details[1].map(d => d.toString()),
      totalTeamSize: details[2].toString()
    };
  } catch (error) {
    console.error('Error getting matrix details:', error);
    return { upline: '0', directDownlines: [], totalTeamSize: '0' };
  }
};

export const getUserTotalClaimableIncome = async (userId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const income = await contract.getUserTotalClaimableIncome(userId);
    
    return {
      regularIncome: income[0].toString(),
      poolIncome: income[1].toString(),
      royaltyIncome: income[2].toString(),
      totalClaimable: income[3].toString()
    };
  } catch (error) {
    console.error('Error getting claimable income:', error);
    throw error;
  }
};

const checkAndApproveUSDT = async (amount, account) => {
  try {
    const provider = getProvider();
    const signer = provider.getSigner();
    const usdtContract = getUSDTContract(signer);
    
    console.log('Checking USDT allowance...');
    console.log('USDT Contract:', USDT_ADDRESS);
    console.log('Main Contract:', CONTRACT_ADDRESS);
    console.log('Amount needed:', ethers.utils.formatEther(amount));
    
    // Check current allowance
    let allowance;
    try {
      allowance = await usdtContract.allowance(account, CONTRACT_ADDRESS);
      console.log('Current allowance:', ethers.utils.formatEther(allowance));
    } catch (error) {
      console.error('Error checking allowance:', error);
      throw new Error('Failed to check USDT allowance. Make sure you have USDT tokens.');
    }
    
    // If allowance is insufficient, request approval
    if (allowance.lt(amount)) {
      console.log('Requesting USDT approval for:', ethers.utils.formatEther(amount), 'USDT');
      
      try {
        // Approve the exact amount or slightly more
        const approvalAmount = amount.mul(10); // Approve 10x the amount for future transactions
        
        console.log('Estimating gas for approval...');
        const gasEstimate = await usdtContract.estimateGas.approve(CONTRACT_ADDRESS, approvalAmount);
        console.log('Gas estimate:', gasEstimate.toString());
        
        const approveTx = await usdtContract.approve(CONTRACT_ADDRESS, approvalAmount, {
          gasLimit: gasEstimate.mul(120).div(100) // 20% buffer
        });
        
        console.log('Approval transaction sent:', approveTx.hash);
        console.log('Waiting for confirmation...');
        
        const receipt = await approveTx.wait();
        console.log('Approval confirmed in block:', receipt.blockNumber);
        
        if (receipt.status === 0) {
          throw new Error('Approval transaction failed');
        }
        
        // Verify approval worked
        const newAllowance = await usdtContract.allowance(account, CONTRACT_ADDRESS);
        console.log('New allowance:', ethers.utils.formatEther(newAllowance));
        
        if (newAllowance.lt(amount)) {
          throw new Error('Approval succeeded but allowance is still insufficient');
        }
        
        console.log('USDT approved successfully!');
      } catch (error) {
        console.error('Approval transaction failed:', error);
        
        if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
          throw new Error('Approval cancelled by user');
        }
        
        throw new Error('Failed to approve USDT: ' + (error.message || 'Unknown error'));
      }
    } else {
      console.log('Sufficient allowance already exists');
    }
    
    return true;
  } catch (error) {
    console.error('Error in checkAndApproveUSDT:', error);
    throw error;
  }
};

export const registerUser = async (referrerId, account) => {
  try {
    const provider = getProvider();
    const signer = provider.getSigner();
    
    console.log('Starting registration...');
    console.log('Referrer ID:', referrerId);
    console.log('Account:', account);
    
    const packagePrice = ethers.BigNumber.from(PACKAGE_PRICES[0]);
    console.log('Package price:', ethers.utils.formatEther(packagePrice), 'USDT');
    
    // Check USDT balance first
    const usdtContract = getUSDTContract(provider);
    try {
      const balance = await usdtContract.balanceOf(account);
      console.log('USDT Balance:', ethers.utils.formatEther(balance));
      
      if (balance.lt(packagePrice)) {
        throw new Error(`Insufficient USDT balance. You need ${ethers.utils.formatEther(packagePrice)} USDT but have ${ethers.utils.formatEther(balance)} USDT`);
      }
    } catch (error) {
      if (error.message.includes('Insufficient USDT')) {
        throw error;
      }
      console.error('Error checking USDT balance:', error);
      throw new Error('Could not verify USDT balance. Make sure you have USDT tokens.');
    }
    
    // Approve USDT
    await checkAndApproveUSDT(packagePrice, account);
    
    // Wait a moment for approval to propagate
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Verify approval one more time
    const finalAllowance = await usdtContract.allowance(account, CONTRACT_ADDRESS);
    console.log('Final allowance before register:', ethers.utils.formatEther(finalAllowance));
    if (finalAllowance.lt(packagePrice)) {
      throw new Error('USDT approval failed. Please try again.');
    }
    
    // Try to call the function statically first to check for errors
    const contract = getContract(provider);
    try {
      console.log('Testing registration call...');
      await contract.callStatic.register(referrerId, { from: account });
      console.log('Static call succeeded, proceeding with actual transaction...');
    } catch (staticError) {
      console.error('Static call failed:', staticError);
      
      // Try to extract error message
      if (staticError.errorName) {
        throw new Error(`Contract error: ${staticError.errorName}`);
      }
      if (staticError.reason) {
        throw new Error(`Contract error: ${staticError.reason}`);
      }
      if (staticError.message.includes('AlreadyRegistered')) {
        throw new Error('You are already registered');
      }
      if (staticError.message.includes('transfer amount exceeds balance')) {
        throw new Error('Insufficient USDT balance');
      }
      if (staticError.message.includes('transfer amount exceeds allowance')) {
        throw new Error('USDT not approved. Please approve USDT first.');
      }
      
      throw new Error('Registration validation failed: ' + staticError.message);
    }
    
    // Register with dynamic gas
    console.log('Estimating gas for registration...');
    const contractWithSigner = getContract(signer);
    
    let gasEstimate;
    try {
      gasEstimate = await contractWithSigner.estimateGas.register(referrerId);
      console.log('Gas estimate:', gasEstimate.toString());
    } catch (gasError) {
      console.error('Gas estimation failed:', gasError);
      throw new Error('Cannot estimate gas. Transaction would likely fail: ' + gasError.message);
    }
    
    console.log('Calling register function...');
    const tx = await contractWithSigner.register(referrerId, {
      gasLimit: gasEstimate.mul(150).div(100) // 50% buffer
    });
    console.log('Registration transaction sent:', tx.hash);
    console.log('View on explorer: https://testnet.opbnbscan.com/tx/' + tx.hash);
    
    console.log('Waiting for confirmation...');
    const receipt = await tx.wait();
    console.log('Transaction confirmed in block:', receipt.blockNumber);
    
    if (receipt.status === 0) {
      throw new Error('Transaction failed. Check transaction: https://testnet.opbnbscan.com/tx/' + tx.hash);
    }
    
    console.log('Registration successful!');
    
    return tx;
  } catch (error) {
    console.error('Error registering:', error);
    
    if (error.message.includes('Insufficient USDT')) {
      throw error;
    }
    
    if (error.message.includes('already registered')) {
      throw new Error('You are already registered');
    }
    
    if (error.message.includes('Approval cancelled')) {
      throw new Error('USDT approval cancelled. Please approve USDT to continue.');
    }
    
    if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
      throw new Error('Transaction cancelled by user');
    }
    
    if (error.reason) {
      throw new Error(error.reason);
    }
    
    throw new Error(error.message || 'Registration failed. Please try again.');
  }
};

export const upgradePackage = async (packageId, account) => {
  try {
    const provider = getProvider();
    const signer = provider.getSigner();
    
    console.log('Starting upgrade to package', packageId);
    
    const packagePrice = ethers.BigNumber.from(PACKAGE_PRICES[packageId - 1]);
    console.log('Package price:', ethers.utils.formatEther(packagePrice), 'USDT');
    
    // Check USDT balance
    const usdtContract = getUSDTContract(provider);
    const balance = await usdtContract.balanceOf(account);
    console.log('USDT Balance:', ethers.utils.formatEther(balance));
    
    if (balance.lt(packagePrice)) {
      throw new Error(`Insufficient USDT balance. You need ${ethers.utils.formatEther(packagePrice)} USDT`);
    }
    
    // Approve USDT
    await checkAndApproveUSDT(packagePrice, account);
    
    // Wait for approval to propagate
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Test call first
    const contract = getContract(provider);
    try {
      await contract.callStatic.upgradePackage(packageId, { from: account });
    } catch (staticError) {
      console.error('Static call failed:', staticError);
      if (staticError.message.includes('PreviousNotActive')) {
        throw new Error('Please activate the previous package first');
      }
      if (staticError.message.includes('AlreadyActive')) {
        throw new Error('This package is already active');
      }
      throw new Error('Upgrade validation failed: ' + staticError.message);
    }
    
    // Upgrade with dynamic gas
    console.log('Estimating gas...');
    const contractWithSigner = getContract(signer);
    const gasEstimate = await contractWithSigner.estimateGas.upgradePackage(packageId);
    console.log('Gas estimate:', gasEstimate.toString());
    
    const tx = await contractWithSigner.upgradePackage(packageId, {
      gasLimit: gasEstimate.mul(150).div(100)
    });
    console.log('Upgrade transaction sent:', tx.hash);
    console.log('View on explorer: https://testnet.opbnbscan.com/tx/' + tx.hash);
    
    const receipt = await tx.wait();
    if (receipt.status === 0) {
      throw new Error('Transaction failed');
    }
    
    console.log('Upgrade successful!');
    return tx;
  } catch (error) {
    console.error('Error upgrading package:', error);
    
    if (error.message.includes('Insufficient USDT')) {
      throw error;
    }
    
    if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
      throw new Error('Transaction cancelled by user');
    }
    
    if (error.reason) {
      throw new Error(error.reason);
    }
    
    throw new Error(error.message || 'Upgrade failed');
  }
};

export const retopupPackage = async (packageId, account) => {
  try {
    const provider = getProvider();
    const signer = provider.getSigner();
    
    console.log('Starting retopup for package', packageId);
    
    const packagePrice = ethers.BigNumber.from(PACKAGE_PRICES[packageId - 1]);
    console.log('Package price:', ethers.utils.formatEther(packagePrice), 'USDT');
    
    // Check USDT balance
    const usdtContract = getUSDTContract(provider);
    const balance = await usdtContract.balanceOf(account);
    console.log('USDT Balance:', ethers.utils.formatEther(balance));
    
    if (balance.lt(packagePrice)) {
      throw new Error(`Insufficient USDT balance. You need ${ethers.utils.formatEther(packagePrice)} USDT`);
    }
    
    // Approve USDT
    await checkAndApproveUSDT(packagePrice, account);
    
    // Wait for approval to propagate
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Test call first
    const contract = getContract(provider);
    try {
      await contract.callStatic.retopupPackage(packageId, { from: account });
    } catch (staticError) {
      console.error('Static call failed:', staticError);
      if (staticError.message.includes('UserNotActive')) {
        throw new Error('This package is not active');
      }
      throw new Error('Retopup validation failed: ' + staticError.message);
    }
    
    // Retopup with dynamic gas
    console.log('Estimating gas...');
    const contractWithSigner = getContract(signer);
    const gasEstimate = await contractWithSigner.estimateGas.retopupPackage(packageId);
    console.log('Gas estimate:', gasEstimate.toString());
    
    const tx = await contractWithSigner.retopupPackage(packageId, {
      gasLimit: gasEstimate.mul(150).div(100)
    });
    console.log('Retopup transaction sent:', tx.hash);
    console.log('View on explorer: https://testnet.opbnbscan.com/tx/' + tx.hash);
    
    const receipt = await tx.wait();
    if (receipt.status === 0) {
      throw new Error('Transaction failed');
    }
    
    console.log('Retopup successful!');
    return tx;
  } catch (error) {
    console.error('Error retopping up:', error);
    
    if (error.message.includes('Insufficient USDT')) {
      throw error;
    }
    
    if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
      throw new Error('Transaction cancelled by user');
    }
    
    if (error.reason) {
      throw new Error(error.reason);
    }
    
    throw new Error(error.message || 'Retopup failed');
  }
};

export const claimAllIncome = async () => {
  try {
    const provider = getProvider();
    const signer = provider.getSigner();
    const contract = getContract(signer);
    
    console.log('Claiming all income...');
    const gasEstimate = await contract.estimateGas.claimAllIncome();
    console.log('Gas estimate:', gasEstimate.toString());
    
    const tx = await contract.claimAllIncome({
      gasLimit: gasEstimate.mul(120).div(100)
    });
    console.log('Claim all income transaction sent:', tx.hash);
    
    const receipt = await tx.wait();
    if (receipt.status === 0) throw new Error('Transaction failed');
    console.log('Claim successful');
    
    return tx;
  } catch (error) {
    console.error('Error claiming income:', error);
    if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
      throw new Error('Transaction cancelled by user');
    }
    if (error.reason) throw new Error(error.reason);
    throw new Error(error.message || 'Claim failed');
  }
};

export const claimIncome = async () => {
  try {
    const provider = getProvider();
    const signer = provider.getSigner();
    const contract = getContract(signer);
    
    console.log('Claiming income...');
    const gasEstimate = await contract.estimateGas.claimIncome();
    console.log('Gas estimate:', gasEstimate.toString());
    
    const tx = await contract.claimIncome({
      gasLimit: gasEstimate.mul(120).div(100)
    });
    console.log('Claim income transaction sent:', tx.hash);
    
    const receipt = await tx.wait();
    if (receipt.status === 0) throw new Error('Transaction failed');
    console.log('Claim successful');
    
    return tx;
  } catch (error) {
    console.error('Error claiming income:', error);
    if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
      throw new Error('Transaction cancelled by user');
    }
    if (error.reason) throw new Error(error.reason);
    throw new Error(error.message || 'Claim failed');
  }
};

export const claimPoolIncome = async (packageId) => {
  try {
    const provider = getProvider();
    const signer = provider.getSigner();
    const contract = getContract(signer);
    
    const tx = await contract.claimPoolIncome(packageId);
    console.log('Claim pool income transaction sent:', tx.hash);
    
    await tx.wait();
    console.log('Pool claim successful');
    
    return tx;
  } catch (error) {
    console.error('Error claiming pool income:', error);
    if (error.reason) throw new Error(error.reason);
    throw new Error(error.message || 'Pool claim failed');
  }
};

export const claimRoyaltyIncome = async () => {
  try {
    const provider = getProvider();
    const signer = provider.getSigner();
    const contract = getContract(signer);
    
    const tx = await contract.claimRoyaltyIncome();
    console.log('Claim royalty income transaction sent:', tx.hash);
    
    await tx.wait();
    console.log('Royalty claim successful');
    
    return tx;
  } catch (error) {
    console.error('Error claiming royalty income:', error);
    if (error.reason) throw new Error(error.reason);
    throw new Error(error.message || 'Royalty claim failed');
  }
};

export const getUserClaimablePoolIncome = async (userId, packageId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const income = await contract.getUserClaimablePoolIncome(userId, packageId);
    return income.toString();
  } catch (error) {
    console.error('Error getting claimable pool income:', error);
    return '0';
  }
};

export const getPoolEligibility = async (userId, packageId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const eligible = await contract.poolEligibility(userId, packageId);
    return eligible;
  } catch (error) {
    console.error('Error checking pool eligibility:', error);
    return false;
  }
};

export const getTotalTeamSize = async (userId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    
    let totalTeam = 0;
    
    // Get team size from all packages
    for (let packageId = 1; packageId <= 8; packageId++) {
      try {
        const matrixDetails = await contract.getMatrixDetails(userId, packageId);
        const teamSize = Number(matrixDetails[2].toString());
        if (teamSize > totalTeam) {
          totalTeam = teamSize;
        }
      } catch (err) {
        console.error(`Error getting team for package ${packageId}:`, err);
      }
    }
    
    return totalTeam;
  } catch (error) {
    console.error('Error getting total team size:', error);
    return 0;
  }
};

export const getCurrentPackage = (packages) => {
  // Find highest active package
  for (let i = 7; i >= 0; i--) {
    if (packages[i]) {
      return i + 1;
    }
  }
  return 0;
};
export const getMatrixLevelUsers = async (userId, packageId, level) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const users = await contract.getMatrixLevelUsers(userId, packageId, level);
    return users.map(u => u.toString());
  } catch (error) {
    console.error('Error getting matrix level users:', error);
    return [];
  }
};

export const getNormalLevelUsers = async (userId, packageId, level) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const users = await contract.getNormalLevelUsers(userId, packageId, level);
    return users.map(u => u.toString());
  } catch (error) {
    console.error('Error getting normal level users:', error);
    return [];
  }
};

export const getMatrixLevelCounts = async (userId, packageId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const counts = await contract.getMatrixLevelCounts(userId, packageId);
    return counts.map(c => Number(c.toString()));
  } catch (error) {
    console.error('Error getting matrix level counts:', error);
    return new Array(10).fill(0);
  }
};

export const getNormalLevelCounts = async (userId, packageId) => {
  try {
    const provider = getProvider();
    const contract = getContract(provider);
    const counts = await contract.getNormalLevelCounts(userId, packageId);
    return counts.map(c => Number(c.toString()));
  } catch (error) {
    console.error('Error getting normal level counts:', error);
    return new Array(10).fill(0);
  }
};

export const getUserActivities = async (userId) => {
  try {
    // Get provider first
    const provider = getProvider();
    if (!provider) {
      console.error('Provider not available');
      return [];
    }
    
    const contract = getContract(provider);
    
    // Get current block to limit search range
    const currentBlock = await provider.getBlockNumber();
    const fromBlock = Math.max(0, currentBlock - 10000); // Last 10000 blocks
    
    // Get all relevant events with block range
    const [
      registrationEvents,
      activationEvents,
      retopupEvents,
      directIncomeEvents,
      matrixIncomeEvents,
      normalIncomeEvents,
      claimEvents,
      poolClaimEvents,
      royaltyClaimEvents
    ] = await Promise.all([
      contract.queryFilter(contract.filters.UserRegistered(userId), fromBlock, currentBlock).catch(() => []),
      contract.queryFilter(contract.filters.PackageActivated(userId), fromBlock, currentBlock).catch(() => []),
      contract.queryFilter(contract.filters.PackageRetoppedUp(userId), fromBlock, currentBlock).catch(() => []),
      contract.queryFilter(contract.filters.DirectReferralIncome(null, userId), fromBlock, currentBlock).catch(() => []),
      contract.queryFilter(contract.filters.MatrixLevelIncome(null, userId), fromBlock, currentBlock).catch(() => []),
      contract.queryFilter(contract.filters.NormalLevelIncome(null, userId), fromBlock, currentBlock).catch(() => []),
      contract.queryFilter(contract.filters.Claimed(userId), fromBlock, currentBlock).catch(() => []),
      contract.queryFilter(contract.filters.PoolIncomeClaimed(userId), fromBlock, currentBlock).catch(() => []),
      contract.queryFilter(contract.filters.RoyaltyIncomeClaimed(userId), fromBlock, currentBlock).catch(() => [])
    ]);

    const activities = [];

    // Helper function to get block timestamp
    const getBlockTimestamp = async (blockNumber) => {
      try {
        const block = await provider.getBlock(blockNumber);
        return block.timestamp;
      } catch (error) {
        console.error('Error getting block timestamp:', error);
        return Math.floor(Date.now() / 1000);
      }
    };

    // Process registration events
    for (const event of registrationEvents) {
      const timestamp = await getBlockTimestamp(event.blockNumber);
      activities.push({
        type: 'registration',
        timestamp: timestamp,
        toId: event.args.userId.toString(),
        fromId: event.args.referrerId.toString()
      });
    }

    // Process activation events
    for (const event of activationEvents) {
      const timestamp = await getBlockTimestamp(event.blockNumber);
      activities.push({
        type: 'activation',
        packageId: parseInt(event.args.packageId.toString()),
        timestamp: timestamp
      });
    }

    // Process retopup events
    for (const event of retopupEvents) {
      const timestamp = await getBlockTimestamp(event.blockNumber);
      activities.push({
        type: 'retopup',
        packageId: parseInt(event.args.packageId.toString()),
        amount: event.args.amount.toString(),
        timestamp: timestamp
      });
    }

    // Process direct income events
    for (const event of directIncomeEvents) {
      const timestamp = await getBlockTimestamp(event.blockNumber);
      activities.push({
        type: 'direct_income',
        fromId: event.args.fromId.toString(),
        toId: event.args.toId.toString(),
        amount: event.args.amount.toString(),
        packageId: parseInt(event.args.packageId.toString()),
        timestamp: timestamp
      });
    }

    // Process matrix income events
    for (const event of matrixIncomeEvents) {
      const timestamp = await getBlockTimestamp(event.blockNumber);
      activities.push({
        type: 'matrix_income',
        fromId: event.args.fromId.toString(),
        toId: event.args.toId.toString(),
        amount: event.args.amount.toString(),
        level: parseInt(event.args.level.toString()),
        packageId: parseInt(event.args.packageId.toString()),
        timestamp: timestamp
      });
    }

    // Process normal/generation income events
    for (const event of normalIncomeEvents) {
      const timestamp = await getBlockTimestamp(event.blockNumber);
      activities.push({
        type: 'normal_income',
        fromId: event.args.fromId.toString(),
        toId: event.args.toId.toString(),
        amount: event.args.amount.toString(),
        level: parseInt(event.args.level.toString()),
        packageId: parseInt(event.args.packageId.toString()),
        timestamp: timestamp
      });
    }

    // Process claim events
    for (const event of claimEvents) {
      const timestamp = await getBlockTimestamp(event.blockNumber);
      activities.push({
        type: 'claim',
        amount: event.args.amount.toString(),
        timestamp: timestamp
      });
    }

    // Process pool claim events
    for (const event of poolClaimEvents) {
      const timestamp = await getBlockTimestamp(event.blockNumber);
      activities.push({
        type: 'pool_claim',
        packageId: parseInt(event.args.packageId.toString()),
        amount: event.args.amount.toString(),
        timestamp: timestamp
      });
    }

    // Process royalty claim events
    for (const event of royaltyClaimEvents) {
      const timestamp = await getBlockTimestamp(event.blockNumber);
      activities.push({
        type: 'royalty_claim',
        amount: event.args.amount.toString(),
        timestamp: timestamp
      });
    }

    // Sort by timestamp (most recent first)
    activities.sort((a, b) => b.timestamp - a.timestamp);

    return activities;
  } catch (error) {
    console.error('Error fetching user activities:', error);
    return [];
  }
};